import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import api from '../../lib/api';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export const OptionsBetaAcknowledgeModal = () => {
  const { t } = useOptionsLocale();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      try {
        const response = await api.get('/user/profile');
        if (!active) return;
        if (response.data?.options_beta_acknowledged !== true) {
          setOpen(true);
        }
      } catch (error) {
        // Silently ignore; do not block the app if the profile fetch fails.
      }
    };
    loadProfile();
    return () => { active = false; };
  }, []);

  const handleContinue = async () => {
    if (!checked || saving) return;
    setSaving(true);
    setOpen(false);
    try {
      await api.post('/user/options-beta-acknowledged');
    } catch (error) {
      // Non-blocking: still let the user proceed even if persistence fails.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="flex w-[min(94vw,560px)] max-w-[560px] max-h-[calc(100vh-2rem)] flex-col gap-2.5 overflow-hidden border-white/10 bg-[#0A0A0A] p-5 text-white sm:p-6 [&>button]:hidden"
        data-testid="options-beta-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-black tracking-tight text-white" data-testid="options-beta-modal-title">
            {t('optionsBeta.title')}
            <span className="rounded-full border border-[#F0B90B]/30 bg-[#F0B90B]/10 px-2 py-0.5 text-[10px] font-bold tracking-[0.14em] text-[#F7D47A]" data-testid="options-beta-modal-badge">
              {t('optionsBeta.badge')}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">{t('optionsBeta.title')}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto pr-1" data-testid="options-beta-modal-scroll-area">
          <div className="space-y-1.5 text-[13px] leading-[1.45] text-white/70 sm:text-sm" data-testid="options-beta-modal-body">
            <p data-testid="options-beta-modal-body-1">{t('optionsBeta.body1')}</p>
            <p data-testid="options-beta-modal-body-2">{t('optionsBeta.body2')}</p>
            <p data-testid="options-beta-modal-body-3">{t('optionsBeta.body3')}</p>
            <p data-testid="options-beta-modal-body-4">{t('optionsBeta.body4')}</p>
          </div>
        </div>

        <div className="space-y-3 pt-1">
          <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2" data-testid="options-beta-modal-checkbox-label">
            <Checkbox
              checked={checked}
              onCheckedChange={(value) => setChecked(value === true)}
              className="mt-0.5"
              data-testid="options-beta-modal-checkbox"
            />
            <span className="text-sm text-white/85">{t('optionsBeta.checkboxLabel')}</span>
          </label>

          <Button
            type="button"
            onClick={handleContinue}
            disabled={!checked || saving}
            className="w-full"
            data-testid="options-beta-modal-continue-button"
          >
            {t('optionsBeta.continueButton')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OptionsBetaAcknowledgeModal;
